import React, { useCallback, useState } from 'react';
import classes from './style.module.scss';
import useTranslation from "~/app/hooks/useTranslation";
import Icon from "~/app/common/Icon";
import { getType, TrainingDetail, TRAINING_STATUS } from '~/training/types';
import Button from '~/app/common/Button';
import { useStoreState } from '~/store';
import Loader from '~/app/common/Loader';
import ApproveTraining from '../ApproveTraining';
import LightBoxImage from '~/app/common/LightBoxImage';

export interface TrainingProps {
  training: TrainingDetail
}

export type TrainingComponent = React.FC<TrainingProps>

const Training: TrainingComponent = ({ training }) => {

  const [isExternalExpanded, setIsExternalExpanded] = useState(false);
  const [isAttachmentExpanded, setIsAttachmentExpanded] = useState(false);
  const [isTraineeExpanded, setIsTraineeExpanded] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [status, setStatus] = useState<TRAINING_STATUS>(TRAINING_STATUS.PENDING);
  const fetching = useStoreState(state => state.training.fetching);

  const { t } = useTranslation("training");

  const toggleExpand = (section: 'external' | 'attachment' | 'trainee') => {
    switch (section) {
      case 'external':
        setIsExternalExpanded(prevState => !prevState)
        break;
      case 'attachment':
        setIsAttachmentExpanded(prevState => !prevState)
        break;
      case 'trainee':
        setIsTraineeExpanded(prevState => !prevState)
        break;
      default:
        break;
    }
  };

  const handleOnActionClick = useCallback((status: number) => {
    setStatus(status);
    setIsConfirmationOpen(true);
  }, [])

  if (fetching) {
    return <Loader size="32" />
  }

  return (
    <div className={classes['training-info']}>
      <div className={classes['info-wrapper']}>
        <div className={classes['title']}>{t('title')}</div>
        <div className={classes['value']}>{training.title}</div>
  
        <div className={classes['title']}>{t('trainingDate')}</div>
        <div className={classes['value']}>{training.trainingDate}</div>
        {!!training.status &&
          <>
            <div className={classes['title']}>{t("statusTitle")}</div>
            <div className={classes['value']}>
              <Icon name="stop_circle" className={`${classes['status']} ${classes[training.status]}`} />
              {t(`status.${training.status}`)}
            </div>
          </>
        }

        <div className={classes['title']}>{t("instructor")}</div>
        <div className={classes["instructor-info"]}>
          <img className={classes["instructor-avatar"]} src={training.instructor.avatar} alt={training.instructor.name} />
          <div>
            <div className={classes["instructor-name"]}>{training.instructor.name}</div>
            <div className={classes["instructor-email"]}>{training.instructor.email}</div>
          </div>
        </div>
        <div className={classes['title']}>{t("commentFromInstructor")}</div>
        <div className={classes['value']}>{
          training.commentFromInstructor && training.commentFromInstructor.trim()
            ? training.commentFromInstructor
            : t('noCommentAdded')
        }</div>
      </div>
      <hr className={classes['bottom-line']} />

      {/* Training Checklist */}
      <div className={classes["section"]}>
        <div className={classes["title"]}>{t("checklist")}</div>
        <ul className={classes["checklist"]}>
          {training.checklist.map((item) => (
            <li key={item.id} className={classes["checklist-item"]}>
              <span
                className={`${classes["check-icon"]} ${item.isChecked ? classes["done"] : ""}`}
              />
              <span className={classes["check-text"]}>{item.title}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* External Resources */}
      <div className={classes["section"]}>
        <div onClick={() => toggleExpand('external')} className={classes['external-switch']}>
          {t("external.externalContent")}
          {isExternalExpanded ? <Icon name='keyboard_arrow_up' /> : <Icon name='keyboard_arrow_down' />}
        </div>

        {isExternalExpanded && (
          <>
            {training.externalResources?.length > 0 ? (
              <div className={classes["resources"]}>
                {training.externalResources.map((externalResource) => (
                  <a
                    key={externalResource.id}
                    href={externalResource.url}
                    className={classes["resource-item"]}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className={classes["resource-info"]}>
                      <div className={classes["resource-title"]}>
                        <Icon name={getType(externalResource.type)} /> <span className={classes["resource-title-text"]}>{externalResource.title}</span>
                      </div>
                    </div>

                    <div className={classes["resource-icon"]}>↗</div>
                  </a>
                ))}
              </div>
            ) : (
              <div className={classes["empty"]}>{t("external.noExternalResourceAdded")}</div>
            )}
          </>
        )}
      </div>

      {/* Attachments */}
      <div className={classes["section"]}>
        <div onClick={() => toggleExpand('attachment')} className={classes['external-switch']}>
          {t("attachments.title")}
          {isAttachmentExpanded ? <Icon name='keyboard_arrow_up' /> : <Icon name='keyboard_arrow_down' />}
        </div>

        {isAttachmentExpanded && (
          training.images?.length > 0 || training.pdfs?.length > 0 ? (
            <div className={classes["attachments"]}>
              {training.images?.length > 0 ? (
                <div>
                  <p className={classes['attachment-label']}>{t("attachments.images")}</p>
                  <div className={classes["attachment-images"]}>
                    {training.images.map((image, index) => (
                      <div key={index} className={classes["training-image"]}>
                        <LightBoxImage image={image.url} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={classes["empty"]}>{t("attachments.noImagesAdded")}</div>
              )}
              {training.pdfs?.length > 0 ? (
                <div>
                  <p className={classes['attachment-label']}>{t("attachments.pdfs")}</p>
                  <div className={classes["resources"]}>
                    {training.pdfs.map((pdf, index) => (
                      <a
                        key={index}
                        href={pdf.url}
                        className={classes["resource-item"]}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <div className={classes["resource-info"]}>
                          <div className={classes["resource-title"]}>
                            <Icon name='picture_as_pdf' /> {pdf.name}
                          </div>
                        </div>

                        <div className={classes["resource-icon"]}>↗</div>
                      </a>
                    ))}
                  </div>
                </div>

              ) : (
                <div className={classes["empty"]}>{t("attachments.noPDFAdded")}</div>
              )}
            </div>
          ) : (
            <div className={classes["empty"]}>{t("attachments.noAttachmentAdded")}</div>
          )
        )}
      </div>

      {/* Trainees */}
      <div className={classes["section"]}>
        <div onClick={() => toggleExpand('trainee')} className={classes['external-switch']}>
          {t("participants")}
          {isTraineeExpanded ? <Icon name='keyboard_arrow_up' /> : <Icon name='keyboard_arrow_down' />}
        </div>
        {isTraineeExpanded && (
          <>
            <ul className={classes["trainee-list"]}>
              {training.participants.map((trainee) => (
                <li key={trainee.id} className={classes["trainee-item"]}>
                  <div className={classes["trainee-info"]}>
                    <div className={classes["trainee-name"]}>{trainee.name}</div>
                    <div className={classes["trainee-email"]}>{trainee.email}</div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* comment */}
      <div className={classes['section']}>
        <div className={classes['title']}>{t('comment')}</div>
        <div className={classes['value']}>
          {training.comment && training.comment.trim()
            ? training.comment
            : t('noCommentAdded')}
        </div>
      </div>

      {training.status === TRAINING_STATUS.PENDING &&
        <>
          <div className={`${classes['footer']}`}>
            <Button className={classes['approve']} onClick={() => handleOnActionClick(TRAINING_STATUS.APPROVE)}>{t('approve')}</Button>
            <Button className={classes['reject']} onClick={() => handleOnActionClick(TRAINING_STATUS.REJECT)}>{t('reject')}</Button>
          </div>
          <ApproveTraining open={isConfirmationOpen} status={status} onClose={() => setIsConfirmationOpen(false)} />
        </>
      }

    </div>
  );
};

export default Training
